import React, { useContext, useMemo, useState } from "react"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  Stack,
  Typography,
} from "@mui/material"

import { useFormik } from "formik";

import { ActiveStatus, MeetingStatus, ProposedTimeSlot, Timeslot } from "../../../types"

import toast from "react-hot-toast";

import {
  useAddMeeting,
  useListOrganisations,
  useListPatients,
  useListUsers,
} from "../../../store";
import { AuthContext } from "../../../context/auth-context";
import {
  getScheduleMeetingModalErrors,
  MeetingFormValues,
  MODAL_ERROR_SX,
  validateMeetingForm,
} from "../common/form-utils";
import AttendeesSelect from "../../attendees/attendee-select";
import FlexibleTimeCalendar from "../common/flexible-time-calendar";
//import FinaliserSelect from "../common/finaliser-select";
import PatientSelect from "../common/patient-select";
import PatientChip from "../../chips/patient-chip";
import TimeSummaryTag from "../common/time-summary-tag";
import { UserAvatar } from "../../avatars";

interface ScheduleMeetingModalProps {
  open: boolean
  startTime?: Date
  patientId?: string
  handleClose: () => void
}

const DEFAULT_MEETING_DURATION_MINUTES = 30;

const ScheduleMeetingModal: React.FC<ScheduleMeetingModalProps> = ({
  open,
  startTime,
  patientId,
  handleClose,
}: ScheduleMeetingModalProps) => {

  const summaryTagBaseSx = {
    px: 0.5,
    py: 2,
    fontSize: "1.15rem",
    fontWeight: 600,
    borderRadius: 999,
  };

  const hasExplicitStartTime = !!startTime && !Number.isNaN(startTime.getTime());

  const addSuccess = () => {
    toast.success("Meeting successfully added");
  }

  const addError = () => {
    toast.error("Error adding meeting")
  }

  const { userId } = useContext(AuthContext);

  const getInitialStep = () => {
    if (patientId) {
      return 2;
    }

    return 1;
  };

  const [activeStep, setActiveStep] = useState<number>(getInitialStep());
  const [waitingOnAdd, setWaitingOnAdd] = useState<boolean>(false);
  const [hasClickedFinalisationWithoutTime, setHasClickedFinalisationWithoutTime] = useState(false);

  React.useEffect(() => {
    if (open) {
      setActiveStep(getInitialStep());
      setHasClickedFinalisationWithoutTime(false);
    }
  }, [open, patientId, hasExplicitStartTime]);

  const { data: patients } = useListPatients();
  const { data: users } = useListUsers();
  const { data: teams = [] } = useListOrganisations();
  const { mutateAsync: addMeeting } = useAddMeeting(addSuccess, addError);

  const activePatients = useMemo(() => {
    return (patients || []).filter((patient) => patient.status === ActiveStatus.Active);
  }, [patients]);

  const formik = useFormik<MeetingFormValues>({
    enableReinitialize: true,
    initialValues: {
      startTime: hasExplicitStartTime ? new Date(startTime) : (null as any),
      meetingType: MeetingStatus.Scheduled,
      organisationIds: [],
      patientId: patientId || "",
      attendeeIds: userId ? [userId] : [],
      notifyTimeAttendees: [],
      inviteEmails: [],
      proposedTimes: [],
    },
    validate: (values) => validateMeetingForm(values, {
      userId,
      users,
    }),
    onSubmit: async (values, helpers) => {
      setWaitingOnAdd(true);
      helpers.setFieldTouched("inviteEmails", true, false);
      helpers.setFieldError("inviteEmails", undefined);

      try {
        await addMeeting(values)
        helpers.resetForm();
        handleClose();
      } finally {
        setWaitingOnAdd(false);
      }
    },
  });

  const onClose = () => {
    setActiveStep(1);
    setHasClickedFinalisationWithoutTime(false);
    formik.resetForm();
    handleClose()
  }

  const changePatient = (selected: string) => {
    formik.setTouched({ ...formik.touched, patientId: true });
    formik.setFieldError("patientId", undefined);
    formik.setFieldValue("patientId", selected);

    if (selected) {
      setActiveStep(2);
    }
  };

  const attendeeChangeEvent = (attendeeIds: string[]) => {
    formik.setTouched({ ...formik.touched, attendeeIds: true });
    formik.setFieldError("inviteEmails", undefined);
    formik.setFieldError("attendeeIds", undefined);
    formik.setFieldValue("attendeeIds", attendeeIds);
  };

  const teamChangeEvent = (organisationIds: string[]) => {
    formik.setTouched({ ...formik.touched, organisationIds: true });
    formik.setFieldError("organisationIds", undefined);
    formik.setFieldValue("organisationIds", organisationIds);
  }

  const inviteEmailChangeEvent = (inviteEmails: string[]) => {
    formik.setTouched({ ...formik.touched, inviteEmails: true });
    formik.setFieldError("inviteEmails", undefined);
    formik.setFieldValue("inviteEmails", inviteEmails);
  };

  const getSelectedSlotCount = (slots: ProposedTimeSlot[]) => {
    return slots.length;
  };

  const timeSelectionChangeEvent = (selectedSlots: Timeslot[]) => {
    const proposedSlots: ProposedTimeSlot[] = selectedSlots.map((slot) => ({
      creatorId: userId,
      startTime: slot.startTime,
      endTime: slot.endTime,
    }));

    const nextStartTime = proposedSlots.length > 0 ? proposedSlots[0].startTime : null;
    const selectedSlotCount = getSelectedSlotCount(proposedSlots);
    const isMultipleSelection = selectedSlotCount > 1;

    formik.setTouched({ ...formik.touched, startTime: true, proposedTimes: true });
    formik.setFieldError("startTime", undefined);
    formik.setFieldError("proposedTimes", undefined);
    formik.setFieldValue("meetingType", isMultipleSelection ? MeetingStatus.Proposed : MeetingStatus.Scheduled);
    formik.setFieldValue("proposedTimes", isMultipleSelection ? proposedSlots : []);
    formik.setFieldValue("startTime", nextStartTime as any);
  };

  const selectedTimeValue = useMemo<ProposedTimeSlot[]>(() => {
    if ((formik.values.proposedTimes?.length || 0) > 0) {
      return formik.values.proposedTimes ?? [];
    }

    if (!formik.values.startTime || Number.isNaN(formik.values.startTime.getTime())) {
      return [];
    }

    return [{
      creatorId: userId,
      startTime: formik.values.startTime,
      endTime: new Date(formik.values.startTime.getTime() + 15 * 60000),
    }];
  }, [formik.values.proposedTimes, formik.values.startTime, userId]);

  const selectedCalendarValue = useMemo<Timeslot[]>(() => {
    return selectedTimeValue.map((slot) => ({
      startTime: slot.startTime,
      endTime: slot.endTime,
    }));
  }, [selectedTimeValue]);

  const hasPatientSelected = !!formik.values.patientId;
  const hasSpecificDateTime = !!formik.values.startTime;
  const hasFlexibleTimeSlots = (formik.values.proposedTimes?.length || 0) > 0;
  const hasAnySelectedTime = hasSpecificDateTime || hasFlexibleTimeSlots;
  const selectedTimeslotCount = getSelectedSlotCount(selectedTimeValue || []);
  const hasMultipleProposedSlots = selectedTimeslotCount > 1;

  const { modalErrors, attendeeFieldError, canSubmit: canScheduleMeeting } = getScheduleMeetingModalErrors({
    activeStep,
    hasClickedFinalisationWithoutTime,
    hasPatientSelected,
    hasAnySelectedTime,
    attendeeIds: formik.values.attendeeIds,
    inviteEmails: formik.values.inviteEmails,
    formikErrors: {
      patientId: formik.errors.patientId,
      startTime: formik.errors.startTime,
      attendeeIds: formik.errors.attendeeIds,
      inviteEmails: formik.errors.inviteEmails,
    },
  });

  const submitDisabled = waitingOnAdd || formik.isSubmitting || !canScheduleMeeting;

  const selectedPatientLabel = useMemo(() => {
    if (!formik.values.patientId) {
      return "No patient selected";
    }

    const selectedPatient = patients?.find((item) => item.patientId === formik.values.patientId);
    return selectedPatient?.displayId || formik.values.patientId;
  }, [formik.values.patientId, patients]);

  const selectedAttendeeSummary = useMemo(() => {
    const summaryItems: Array<{
      id: string;
      initials: string;
      color: string;
      shape: "round" | "square";
      label: string;
      orgs: string[];
    }> = [];

    const newUserItems = formik.values.attendeeIds
      .map((id) => users?.find((user) => user.userId === id))
      .filter((user): user is NonNullable<typeof user> => !!user)
      .map((user) => ({
        id: user.userId,
        initials: (user.fullName || user.email || "U").split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "U",
        color: "#64748b",
        shape: "round" as const,
        label: user.fullName || user.email,
        orgs: (user.organisationIds || [])
          .map((teamId) => teams.find((team) => team.organisationId === teamId)?.color || "")
          .filter(Boolean),
      }));

    const teamItems = formik.values.organisationIds
      .map((teamId) => teams.find((team) => team.organisationId === teamId))
      .filter((team): team is NonNullable<typeof team> => !!team)
      .map((team) => ({
        id: team.organisationId,
        initials: (team.name || "ORG").split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "ORG",
        color: team.color || "#1976d2",
        shape: "square" as const,
        label: team.name,
        orgs: [team.color || "#1976d2"],
      }));

    const emailItems = formik.values.inviteEmails
      .filter((email) => !!email)
      .map((email) => ({
        id: `email-${email}`,
        initials: (email.split("@")[0] || "E").slice(0, 2).toUpperCase(),
        color: "#64748b",
        shape: "round" as const,
        label: email,
        orgs: [],
      }));

    summaryItems.push(...newUserItems, ...emailItems, ...teamItems);
    return summaryItems;
  }, [formik.values.attendeeIds, formik.values.inviteEmails, formik.values.organisationIds, teams, users]);

  const selectedTeamWarnings = useMemo(() => {
    const selectedUsers = formik.values.attendeeIds
      .map((id) => users?.find((user) => user.userId === id))
      .filter((user): user is NonNullable<typeof user> => !!user);

    return formik.values.organisationIds
      .map((teamId) => teams.find((team) => team.organisationId === teamId))
      .filter((team): team is NonNullable<typeof team> => !!team)
      .filter((team) => {
        return !selectedUsers.some((user) => (user.organisationIds || []).includes(team.organisationId));
      });
  }, [formik.values.attendeeIds, formik.values.organisationIds, teams, users]);

  const attendeeSummaryAvatars = selectedAttendeeSummary.slice(0, 4);
  const extraAttendeeCount = selectedAttendeeSummary.length - attendeeSummaryAvatars.length;
  const isMeetingFinalisationEnabled = hasMultipleProposedSlots;
  const finaliserOptions = useMemo(() => {
    const uniqueAttendeeIds = Array.from(new Set(formik.values.attendeeIds.filter(Boolean)));

    return uniqueAttendeeIds
      .filter((attendeeId) => attendeeId !== userId)
      .map((attendeeId) => {
        const attendee = users?.find((user) => user.userId === attendeeId);
        const teamBadges = (attendee?.organisationIds || [])
          .map((teamId) => teams.find((team) => team.organisationId === teamId))
          .filter((team): team is NonNullable<typeof team> => !!team)
          .map((team) => ({
            id: team.organisationId,
            name: team.name,
            color: team.color || "#1976d2",
          }));

        return {
          userId: attendeeId,
          label: attendee?.fullName || attendee?.email || attendeeId,
          email: attendee?.email,
          teamBadges,
        };
      });
  }, [formik.values.attendeeIds, teams, userId, users]);
  const selectedFinaliserIds = formik.values.notifyTimeAttendees || [];
  const selectedFinaliserOptions = useMemo(() => {
    return finaliserOptions.filter((option) => selectedFinaliserIds.includes(option.userId));
  }, [finaliserOptions, selectedFinaliserIds]);

  React.useEffect(() => {
    const currentnotifyTimeAttendees = formik.values.notifyTimeAttendees || [];

    if (!isMeetingFinalisationEnabled) {
      if (currentnotifyTimeAttendees.length > 0) {
        formik.setFieldValue("notifyTimeAttendees", [], false);
      }

      return;
    }

    const validnotifyTimeAttendees = currentnotifyTimeAttendees.filter((attendeeId) =>
      finaliserOptions.some((option) => option.userId === attendeeId),
    );

    const nextnotifyTimeAttendees = validnotifyTimeAttendees.length > 0
      ? validnotifyTimeAttendees
      : (finaliserOptions[0]?.userId ? [finaliserOptions[0].userId] : []);

    const didnotifyTimeAttendeesChange =
      nextnotifyTimeAttendees.length !== currentnotifyTimeAttendees.length
      || nextnotifyTimeAttendees.some((id, index) => id !== currentnotifyTimeAttendees[index]);

    if (didnotifyTimeAttendeesChange) {
      formik.setFieldValue("notifyTimeAttendees", nextnotifyTimeAttendees, false);
    }
  }, [activeStep, finaliserOptions, formik, formik.values.notifyTimeAttendees, isMeetingFinalisationEnabled]);


  const StepHeader: React.FC<{
    step: number;
    title: string;
    isActive: boolean;
    rightContent?: React.ReactNode;
  }> = ({ step, title, isActive, rightContent }) => (
    <Box
      sx={{
        width: "100%",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "auto 1fr auto" },
        alignItems: "center",
        columnGap: 2,
        rowGap: 1,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{
          width: 38,
          height: 38,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: "1.1rem",
          color: "#fff",
          backgroundColor: isActive ? "#0ea5e9" : "#94a3b8",
          boxShadow: isActive ? "0 8px 18px rgba(14, 165, 233, 0.25)" : "none",
          transition: "all 180ms ease",
        }}>{step}</Box>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: "1.2rem",
            lineHeight: 1.4,
            letterSpacing: "-0.02em",
            color: isActive ? "#0ea5e9" : "#94a3b8",
          }}
        >
          {title}
        </Typography>
      </Stack>

      {rightContent ? (
        <Box
          sx={{
            justifySelf: { md: "end" },
            ml: "auto",
            width: { xs: "100%", md: "auto" },
            maxWidth: "100%",
          }}
          onClick={(event) => {
            if (isActive) {
              event.stopPropagation();
            }
          }}
          onFocus={(event) => {
            if (isActive) {
              event.stopPropagation();
            }
          }}
        >
          {rightContent}
        </Box>
      ) : null}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Stack padding={2.5} spacing={2} sx={{ background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 45%)" }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Schedule Meeting</Typography>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={2}>
            <Accordion
              expanded={activeStep === 1}
              onChange={() => setActiveStep(1)}
              disableGutters
              sx={{ borderRadius: 2, border: "1px solid #dbe7f5", boxShadow: "0 3px 14px rgba(15, 23, 42, 0.05)", overflow: "hidden" }}
            >
              <AccordionSummary
                sx={{
                  pr: 0,
                  pl: 2,
                  py: 2,
                  "& .MuiAccordionSummary-content": {
                    width: "100%",
                    margin: "0 !important",
                  },
                }}
              >
                <StepHeader
                  step={1}
                  title="Patient"
                  isActive={activeStep === 1}
                  rightContent={
                    activeStep !== 1 ? (
                      <Stack direction="row" justifyContent="flex-end">
                        {formik.values.patientId ? (
                          <PatientChip label={selectedPatientLabel} />
                        ) : (
                          <Chip
                            label="No patient selected"
                            size="small"
                            sx={{
                              ...summaryTagBaseSx,
                              color: "#b91c1c",
                              backgroundColor: "#fee2e2",
                            }}
                          />
                        )}
                      </Stack>
                    ) : null
                  }
                />
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1.5}>
                  <PatientSelect
                    value={formik.values.patientId}
                    patients={activePatients}
                    disabled={waitingOnAdd}
                    size="small"
                    onChange={changePatient}
                  />
                </Stack>
              </AccordionDetails>

            </Accordion>

            <Accordion
              expanded={activeStep === 2}
              onChange={() => setActiveStep(2)}
              disableGutters
              sx={{ borderRadius: 2, border: "1px solid #dbe7f5", boxShadow: "0 3px 14px rgba(15, 23, 42, 0.05)", overflow: "hidden" }}
            >
              <AccordionSummary
                sx={{
                  pr: 0,
                  pl: 2,
                  py: 2,
                  "& .MuiAccordionSummary-content": {
                    width: "100%",
                    margin: "0 !important",
                  },
                }}
              >
                <StepHeader
                  step={2}
                  title="Attendees"
                  isActive={activeStep === 2}
                  rightContent={
                    activeStep !== 2 && selectedAttendeeSummary.length > 0 ? (
                      <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end">
                        {attendeeSummaryAvatars.map((item, index) => (
                          <Stack
                            key={`${item.id}-${index}`}
                            spacing={0.35}
                            alignItems="center"
                            sx={{
                              marginLeft: index === 0 ? 0 : -1,
                            }}
                          >
                            <UserAvatar
                              text={item.label}
                              color="#64748b"
                              sx={{
                                width: 30,
                                height: 30,
                                fontSize: "0.7rem",
                              }}
                            />
                            {item.orgs.length > 0 ? (
                              item.shape !== "square" ? (
                                <Stack direction="row" spacing={0.25} alignItems="center">
                                  {item.orgs.slice(0, 3).map((orgColor, orgIndex) => (
                                    <Box
                                      key={`${item.id}-org-${orgIndex}`}
                                      sx={{
                                        width: 4,
                                        height: 4,
                                        backgroundColor: orgColor,
                                        borderRadius: 0.4,
                                        border: "1px solid rgba(15,23,42,0.15)",
                                      }}
                                    />
                                  ))}
                                </Stack>
                              ) : null
                            ) : (
                              <Box sx={{ width: 24, height: 8 }} />
                            )}
                          </Stack>
                        ))}
                        {extraAttendeeCount > 0 ? (
                          <Box
                            sx={{
                              minWidth: 30,
                              height: 30,
                              borderRadius: "50%",
                              backgroundColor: "#e2e8f0",
                              color: "#0f172a",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              border: "2px solid #fff",
                              marginLeft: -1,
                            }}
                          >
                            +{extraAttendeeCount}
                          </Box>
                        ) : null}
                      </Stack>
                    ) : null
                  }
                />
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1.5}>
                  <AttendeesSelect
                    selectedUserIds={formik.values.attendeeIds}
                    selectedInviteEmails={formik.values.inviteEmails}
                    selectedTeamsIds={formik.values.organisationIds}
                    allowEmptyTeams={true}
                    disabled={waitingOnAdd}
                    onUsersChange={attendeeChangeEvent}
                    onInviteEmailsChange={inviteEmailChangeEvent}
                    onTeamChange={teamChangeEvent}
                    error={attendeeFieldError}
                  />
                  {selectedTeamWarnings.map((team) => (
                    <Box
                      key={`org-warning-${team.organisationId}`}
                      sx={{
                        border: "1px solid #fde68a",
                        borderRadius: 2,
                        backgroundColor: "#fffbeb",
                        px: 1.5,
                        py: 1,
                        color: "#92400e",
                      }}
                    >
                      <Typography sx={{ fontSize: "0.85rem", lineHeight: 1.4 }}>
                        You have added <Box component="span" sx={{ fontWeight: 700 }}>{team.name}</Box>, but no people from it. Currently, nobody will be notified. Consider adding a person who is part of <Box component="span" sx={{ fontWeight: 700 }}>{team.name}</Box>.
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={activeStep === 3}
              onChange={() => setActiveStep(3)}
              disableGutters
              sx={{ borderRadius: 2, border: "1px solid #dbe7f5", boxShadow: "0 3px 14px rgba(15, 23, 42, 0.05)", overflow: "hidden" }}
            >
              <AccordionSummary
                sx={{
                  pr: 0,
                  pl: 2,
                  py: 2,
                  "& .MuiAccordionSummary-content": {
                    width: "100%",
                    margin: "0 !important",
                  },
                }}
              >
                <StepHeader
                  step={3}
                  title="Date & Time"
                  isActive={activeStep === 3}
                  rightContent={
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ flexWrap: "wrap" }}>
                      {selectedTimeValue.length > 0 ? (
                        <TimeSummaryTag startTimes={selectedTimeValue.map((slot) => slot.startTime)} />
                      ) : null}

                      {activeStep === 2 && !hasAnySelectedTime ? (
                        <Chip
                          label="No time selected"
                          size="small"
                          sx={{
                            ...summaryTagBaseSx,
                            backgroundColor: "#fee2e2",
                            color: "#b91c1c",
                          }}
                        />
                      ) : null}
                    </Stack>
                  }
                />
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1.5}>
                  <FlexibleTimeCalendar
                    open={open}
                    allowMultiple={false}
                    preSelectedTimeslots={selectedCalendarValue}
                    preSelectedPatientId={formik.values.patientId}
                    preSelectedAttendeeIds={formik.values.attendeeIds}
                    meetingDurationMinutes={DEFAULT_MEETING_DURATION_MINUTES}
                    disabled={waitingOnAdd}
                    onChange={timeSelectionChangeEvent}
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* <Accordion
              expanded={activeStep === 4}
              onChange={() => {
                if (!hasAnySelectedTime) {
                  setHasClickedFinalisationWithoutTime(true);
                }

                setActiveStep(4);
              }}
              disableGutters
              sx={{ borderRadius: 2, border: "1px solid #dbe7f5", boxShadow: "0 3px 14px rgba(15, 23, 42, 0.05)", overflow: "hidden" }}
            >
              <AccordionSummary
                sx={{
                  pr: 0,
                  pl: 2,
                  py: 2,
                  "& .MuiAccordionSummary-content": {
                    width: "100%",
                    margin: "0 !important",
                  },
                }}
              >
                <StepHeader
                  step={4}
                  title="Meeting Finalisation"
                  isActive={activeStep === 4}
                  rightContent={
                    activeStep !== 4 ? (
                      !isMeetingFinalisationEnabled ? (
                        <Chip
                          icon={<CheckCircleOutlineIcon fontSize="small" />}
                          label="Immediately"
                          size="small"
                          sx={{
                            ...summaryTagBaseSx,
                            backgroundColor: "#dbeafe",
                            color: "#0077e6",
                            ".MuiChip-icon": {
                              color: "#0077e6",
                            },
                          }}
                        />
                      ) : selectedFinaliserOptions.length > 0 ? (
                        <Stack direction="row" spacing={0.75} alignItems="flex-start" justifyContent="flex-end">
                          {selectedFinaliserOptions.slice(0, 3).map((selectedFinaliserOption) => {
                            const finaliserUser = users?.find((user) => user.userId === selectedFinaliserOption.userId);
                            const initials = (finaliserUser?.fullName || finaliserUser?.email || "U")
                              .split(/\s+/)
                              .filter(Boolean)
                              .slice(0, 2)
                              .map((part) => part[0])
                              .join("")
                              .toUpperCase() || "U";
                            const orgColors = (finaliserUser?.teamIds || [])
                              .map((teamId) => teams.find((team) => team.organisationId === teamId)?.color || "")
                              .filter(Boolean)
                              .slice(0, 3);

                            return (
                              <Stack key={selectedFinaliserOption.userId} spacing={0.35} alignItems="center">
                                <Avatar
                                  title={selectedFinaliserOption.label}
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    fontSize: "0.7rem",
                                    fontWeight: 700,
                                    border: "2px solid #fff",
                                    backgroundColor: "#64748b",
                                    color: "#fff",
                                    borderRadius: "50%",
                                  }}
                                >
                                  {initials}
                                </Avatar>
                                {orgColors.length > 0 ? (
                                  <Stack direction="row" spacing={0.25} alignItems="center">
                                    {orgColors.map((orgColor, orgIndex) => (
                                      <Box
                                        key={`${selectedFinaliserOption.userId}-finaliser-org-${orgIndex}`}
                                        sx={{
                                          width: 4,
                                          height: 4,
                                          backgroundColor: orgColor,
                                          borderRadius: 0.4,
                                          border: "1px solid rgba(15,23,42,0.15)",
                                        }}
                                      />
                                    ))}
                                  </Stack>
                                ) : (
                                  <Box sx={{ width: 24, height: 8 }} />
                                )}
                              </Stack>
                            );
                          })}
                          {selectedFinaliserOptions.length > 3 ? (
                            <Box
                              sx={{
                                minWidth: 30,
                                height: 30,
                                borderRadius: "50%",
                                backgroundColor: "#e2e8f0",
                                color: "#0f172a",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                border: "2px solid #fff",
                                marginLeft: -1,
                              }}
                            >
                              +{selectedFinaliserOptions.length - 3}
                            </Box>
                          ) : null}
                        </Stack>
                      ) : (
                        <Chip
                          label="No finaliser selected"
                          size="small"
                          sx={{
                            ...summaryTagBaseSx,
                            backgroundColor: "#fee2e2",
                            color: "#b91c1c",
                          }}
                        />
                      )
                    ) : null
                  }
                />
              </AccordionSummary>
              <AccordionDetails>
                {isMeetingFinalisationEnabled ? (
                  <FinaliserSelect
                    options={finaliserOptions}
                    selectedFinaliserIds={selectedFinaliserIds}
                    disabled={waitingOnAdd}
                    onChange={(nextFinaliserIds) => {
                      formik.setFieldValue("notifyTimeAttendees", nextFinaliserIds);
                    }}
                  />
                ) : (
                  <Typography sx={{ fontSize: "0.9rem", color: "#334155" }}>
                    As less than two times are selected, the meeting time will be finalised immediately
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion> */}

            {modalErrors.map((errorMessage, index) => (
              <Box key={`${errorMessage}-${index}`} sx={MODAL_ERROR_SX}>
                <Typography sx={{ fontSize: "0.85rem", lineHeight: 1.4 }}>
                  {errorMessage}
                </Typography>
              </Box>
            ))}

            <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
              <Button variant="outlined" color="error" onClick={onClose} fullWidth sx={{ flex: 1 }}>
                Cancel
              </Button>
              <Button
                disabled={submitDisabled}
                color="success"
                type="submit"
                variant="contained"
                fullWidth
                sx={{ flex: 1 }}
              >
                {waitingOnAdd ? "Scheduling..." : "Schedule"}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Stack>
    </Dialog >
  )
}

export default ScheduleMeetingModal;
